import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@admin/hooks/usePermissions";
import { ADMIN_LOGIN_PATH } from "@admin/constants/route";
import { ShieldAlert } from "lucide-react";

/**
 * Defense-in-depth admin guard.
 *
 * Strict semantics:
 *   1. While auth state is unresolved → render a NEUTRAL (non-branded) blank
 *      screen. Never flash admin layout / sidebar / theme to unauthenticated
 *      visitors.
 *   2. Re-validate session on mount via /auth/me (in addition to AuthContext's
 *      initial check) so a stale cached `user` from another tab can't bypass.
 *   3. Unauthenticated → redirect to ADMIN_LOGIN_PATH.
 *   4. Authenticated non-admin → silently redirect to "/" (no error message,
 *      so URL-probing doesn't reveal admin app exists).
 *   5. Admin missing the route's required permission → branded 403 page.
 *   6. Inject <meta name="robots" content="noindex,nofollow"> on every admin
 *      page so it cannot be indexed by search engines.
 *   7. Nested guards (route-level permission checks under the app-level
 *      guard) do NOT re-fetch /auth/me: the parent already revalidated in
 *      this mount, so the permission decision is made synchronously from the
 *      resolved user — no blank pane, no redirect on a transient /me error.
 */
const GuardVerifiedContext = createContext(false);

export default function RequirePermission({ permission, children }) {
  const { user, loading, checkAuth } = useAuth();
  const perms = usePermissions();
  const location = useLocation();
  const parentVerified = useContext(GuardVerifiedContext);
  const [revalidated, setRevalidated] = useState(parentVerified);

  // Re-fetch /auth/me on mount of the OUTERMOST admin guard so role/permission
  // changes applied by another admin propagate on every navigation into the
  // admin app. Defense against stale cookies + tampered local state.
  useEffect(() => {
    if (parentVerified) return undefined;
    let alive = true;
    checkAuth().finally(() => { if (alive) setRevalidated(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SEO hardening — admin pages must never be indexed.
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute("content");
    meta.setAttribute("content", "noindex,nofollow,noarchive,nosnippet");
    return () => {
      if (previous == null) {
        meta.parentNode && meta.parentNode.removeChild(meta);
      } else {
        meta.setAttribute("content", previous);
      }
    };
  }, []);

  // 1) Loading — render a neutral white screen. NO admin branding.
  //    A user already resolved in this tab keeps rendering while the mount
  //    revalidation is in flight (it may be retrying a transient backend
  //    failure); a genuine 401 from that check still nulls the user below.
  if (loading || (!revalidated && !user)) {
    return (
      <div className={parentVerified ? "min-h-[40vh]" : "min-h-screen bg-white"} data-testid="admin-auth-resolving" />
    );
  }

  // 2) Unauthenticated → admin login page (only entry point, lives at
  //    REACT_APP_ADMIN_PATH/login which is also non-obvious).
  if (!user) {
    return <Navigate to={ADMIN_LOGIN_PATH} replace state={{ from: location.pathname }} />;
  }

  // 3) Authenticated but NOT an admin → silently kick to user homepage.
  //    Plain "/" so URL probing doesn't disclose the admin app.
  if (user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  // 4) Admin lacking the page's required permission → branded forbidden page.
  if (permission && !perms.has(permission)) {
    return (
      <div className={`grid place-items-center p-8 ${parentVerified ? "min-h-[60vh]" : "min-h-screen bg-slate-50"}`} data-testid="admin-forbidden">
        <div className="max-w-md text-center bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <ShieldAlert size={42} className="mx-auto text-rose-500" />
          <h2 className="mt-3 text-xl font-bold text-[#0A2540]">Permission required</h2>
          <p className="mt-2 text-sm text-slate-600">
            You do not have <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{permission}</code> access.
            Ask a Super Admin to grant it.
          </p>
        </div>
      </div>
    );
  }

  return <GuardVerifiedContext.Provider value>{children}</GuardVerifiedContext.Provider>;
}
